'use client';

import React, {
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import { useColors } from '../theme/ThemeProvider';
import {
  radius,
  fontSize,
  fontWeight,
  space,
  size,
  motion,
} from '../theme/tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps {
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * primary   → filled, accent color (default)
   * secondary → subtle filled, for less prominent actions
   * ghost     → outline, for tertiary actions
   * danger    → red filled, for destructive actions (mode-independent)
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
  /** Escape hatch. Avoid when possible. */
  style?: CSSProperties;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
}

const HEIGHT: Record<ButtonSize, number> = {
  sm: size.button.sm,
  md: size.button.md,
  lg: size.button.lg,
};

const FONT_SIZE: Record<ButtonSize, number> = {
  sm: fontSize.base,
  md: fontSize.md,
  lg: fontSize.lg,
};

const H_PADDING: Record<ButtonSize, number> = {
  sm: space[4],
  md: space[5],
  lg: space[6],
};

export function Button({
  children,
  onClick,
  variant = 'primary',
  size: sizeKey = 'md',
  fullWidth = false,
  disabled = false,
  style,
  type = 'button',
  'aria-label': ariaLabel,
}: ButtonProps) {
  const colors = useColors();

  // Flat solid fills — no gradients, no colored shadows.
  let background: string;
  let textColor: string;
  let border: string;

  switch (variant) {
    case 'primary':
      background = colors.accent;
      textColor = colors.accentText;
      border = '1.5px solid transparent';
      break;
    case 'secondary':
      background = colors.surfaceAlt;
      textColor = colors.text;
      border = '1.5px solid transparent';
      break;
    case 'ghost':
      background = 'transparent';
      textColor = colors.text;
      border = `1.5px solid ${colors.borderStrong}`;
      break;
    case 'danger':
      // Danger stays red regardless of theme mode — users need clear warning.
      background = colors.danger;
      textColor = '#FFFFFF';
      border = '1.5px solid transparent';
      break;
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        height: HEIGHT[sizeKey],
        padding: `0 ${H_PADDING[sizeKey]}px`,
        borderRadius: radius.md,
        border,
        background,
        color: textColor,
        fontWeight: fontWeight.semibold,
        fontSize: FONT_SIZE[sizeKey],
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        width: fullWidth ? '100%' : undefined,
        transition: `transform ${motion.duration.fast}ms ${motion.easing.out}, background ${motion.duration.base}ms, border-color ${motion.duration.base}ms`,
        fontFamily: 'inherit',
        letterSpacing: 0,
        ...style,
      }}
      onMouseDown={e => {
        if (!disabled) e.currentTarget.style.transform = 'scale(0.97)';
      }}
      onMouseUp={e => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {children}
    </button>
  );
}
